/**
 * useStores Hook
 * 
 * Custom React hook for managing stores in the Manager portal.
 * Provides state management, CRUD operations, and error handling.
 * 
 * @module useStores
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { DocumentSnapshot } from 'firebase/firestore';
import { StoreService } from '../services/StoreService';
import {
  Store,
  StoreFilters,
  StoreSort,
  StoreStatistics,
  StorePayload,
  StoreActionResult,
  BulkStoreAction,
  StoreAssignment,
  StoreStatus
} from '../@types/store';
import { Platform } from '../components/common';

/**
 * Cache key for storing stores data
 */
const STORES_CACHE_KEY = 'mckenzie_stores_cache';
const CACHE_VERSION = '1.0';

/**
 * Cache data structure
 */
interface StoresCacheData {
  version: string;
  stores: Store[];
  filters?: StoreFilters;
  sort?: StoreSort;
  hasMore: boolean;
  timestamp: number;
  cacheKey: string; // Unique key based on filters and sort
}

/**
 * Helper functions for localStorage operations
 */
const cacheStorage = {
  getItem(key: string): string | null {
    if (Platform.OS === 'web') {
      try {
        return localStorage.getItem(key);
      } catch (error) {
        console.warn('Error reading from localStorage:', error);
        return null;
      }
    }
    return null;
  },
  
  setItem(key: string, value: string): void {
    if (Platform.OS === 'web') {
      try {
        localStorage.setItem(key, value);
      } catch (error) {
        console.warn('Error writing to localStorage:', error);
        // If storage is full, try to clear old cache
        try {
          localStorage.removeItem(key);
          localStorage.setItem(key, value);
        } catch (e) {
          console.error('Failed to clear and retry cache storage:', e);
        }
      }
    }
  },
  
  removeItem(key: string): void {
    if (Platform.OS === 'web') {
      try {
        localStorage.removeItem(key);
      } catch (error) {
        console.warn('Error removing from localStorage:', error);
      }
    }
  }
};

/**
 * Generate a cache key based on filters and sort
 */
function generateCacheKey(filters?: StoreFilters, sort?: StoreSort): string {
  const filterStr = filters ? JSON.stringify(filters) : 'no-filters';
  const sortStr = sort ? `${sort.field}-${sort.direction}` : 'name-asc';
  return `${filterStr}-${sortStr}`;
}

/**
 * Load stores from cache
 */
function loadFromCache(cacheKey: string): StoresCacheData | null {
  try {
    const cached = cacheStorage.getItem(STORES_CACHE_KEY);
    if (!cached) return null;
    
    const cacheData: StoresCacheData = JSON.parse(cached);
    
    // Check if cache version matches and cache key matches
    if (cacheData.version !== CACHE_VERSION || cacheData.cacheKey !== cacheKey) {
      return null;
    }
    
    // Check if cache is older than 1 hour (optional: can be adjusted)
    const oneHour = 60 * 60 * 1000;
    if (Date.now() - cacheData.timestamp > oneHour) {
      return null;
    }
    
    return cacheData;
  } catch (error) {
    console.warn('Error loading stores from cache:', error);
    return null;
  }
}

/**
 * Save stores to cache
 */
function saveToCache(cacheData: Omit<StoresCacheData, 'version' | 'timestamp'>): void {
  try {
    const dataToCache: StoresCacheData = {
      ...cacheData,
      version: CACHE_VERSION,
      timestamp: Date.now()
    };
    cacheStorage.setItem(STORES_CACHE_KEY, JSON.stringify(dataToCache));
  } catch (error) {
    console.warn('Error saving stores to cache:', error);
  }
}

/**
 * Clear stores cache
 */
function clearCache(): void {
  cacheStorage.removeItem(STORES_CACHE_KEY);
}

/**
 * Hook state interface
 */
interface UseStoresState {
  // Data
  stores: Store[];
  currentStore: Store | null;
  statistics: StoreStatistics | null;
  
  // Pagination
  hasMore: boolean;
  lastDoc?: DocumentSnapshot;
  
  // Loading states
  isLoading: boolean;
  isLoadingStatistics: boolean;
  isPerformingAction: boolean;
  
  // Error state
  error: string | null;
}

/**
 * Hook return type
 */
interface UseStoresReturn extends UseStoresState {
  // Fetch operations
  fetchStores: (filters?: StoreFilters, sort?: StoreSort, pageSize?: number) => Promise<void>;
  fetchStoreById: (storeId: string) => Promise<void>;
  fetchStatistics: () => Promise<void>;
  loadMore: () => Promise<void>;
  
  // CRUD operations
  createStore: (storeData: StorePayload, userId: string) => Promise<StoreActionResult>;
  updateStore: (storeId: string, updates: Partial<StorePayload>, userId: string) => Promise<StoreActionResult>;
  deleteStore: (storeId: string, userId: string, hardDelete?: boolean) => Promise<StoreActionResult>;
  
  // Status operations
  changeStoreStatus: (storeId: string, newStatus: StoreStatus, userId: string) => Promise<StoreActionResult>;
  
  // Assignment operations
  assignSalesRep: (assignment: StoreAssignment, userId: string) => Promise<StoreActionResult>;
  
  // Bulk operations
  performBulkAction: (bulkAction: BulkStoreAction, userId: string) => Promise<StoreActionResult[]>;
  
  // Utility operations
  refreshStores: () => Promise<void>;
  clearError: () => void;
  setCurrentStore: (store: Store | null) => void;
}

/**
 * Custom hook for store management
 * 
 * @returns Store management functions and state
 * 
 * @example
 * ```tsx
 * function StoreManagement() {
 *   const {
 *     stores,
 *     isLoading,
 *     error,
 *     fetchStores,
 *     updateStore
 *   } = useStores();
 * 
 *   useEffect(() => {
 *     fetchStores({ status: 'active' });
 *   }, []);
 * 
 *   // ... render component
 * }
 * ```
 */
export function useStores(): UseStoresReturn {
  // State management
  const [state, setState] = useState<UseStoresState>({
    stores: [],
    currentStore: null,
    statistics: null,
    hasMore: false,
    lastDoc: undefined,
    isLoading: false,
    isLoadingStatistics: false,
    isPerformingAction: false,
    error: null
  });

  // Store current filters and sort for pagination
  const [currentFilters, setCurrentFilters] = useState<StoreFilters | undefined>();
  const [currentSort, setCurrentSort] = useState<StoreSort | undefined>();
  const [pageSize, setPageSize] = useState<number>(50);
  const [skipCache, setSkipCache] = useState<boolean>(false); // Flag to skip cache on refresh

  // Use ref to track current state for async operations (avoids stale closures)
  const stateRef = useRef<UseStoresState>(state);
  const filtersRef = useRef<StoreFilters | undefined>(currentFilters);
  const sortRef = useRef<StoreSort | undefined>(currentSort);
  const pageSizeRef = useRef<number>(pageSize);
  
  // Keep refs in sync with state
  useEffect(() => {
    stateRef.current = state;
  }, [state]);
  
  useEffect(() => {
    filtersRef.current = currentFilters;
  }, [currentFilters]);
  
  useEffect(() => {
    sortRef.current = currentSort;
  }, [currentSort]);
  
  useEffect(() => {
    pageSizeRef.current = pageSize;
  }, [pageSize]);

  /**
   * Fetch stores with filters and sorting
   * Always queries database when filters/search/sort change
   * Only uses cache if filters/search/sort haven't changed
   */
  const fetchStores = useCallback(async (
    filters?: StoreFilters,
    sort?: StoreSort,
    size: number = 20,
    forceRefresh: boolean = false
  ) => {
    try {
      setState(prev => ({ ...prev, isLoading: true, error: null }));
      
      // Check if filters are active (when filters are active, we want to fetch ALL matching stores)
      const hasActiveFilters = !!(
        filters?.customerNo || 
        filters?.storeName || 
        filters?.salesRepId || 
        filters?.city || 
        filters?.state || 
        filters?.salesGroup || 
        filters?.region || 
        filters?.tags
      );
      
      // When filters are active or large pageSize is requested, allow larger page sizes
      // This ensures all filtered results are displayed without pagination
      // For normal browsing without filters, use standard pagination (50 stores)
      // We cap at 9999 to stay within Firestore structured query limits (10000)
      const maxPageSize = (hasActiveFilters || size > 50) ? 9999 : 50;
      const pageSize = Math.min(size, maxPageSize);

      // Prepare filters for the query
      const optimizedFilters = { ...filters };
      
      // When searching, don't apply default status filter to search ALL stores
      // Only add default status filter for non-search queries if not explicitly set
      const isSearching = !!filters?.searchTerm?.trim();
      if (!isSearching && !filters?.status && filters?.status !== 'all') {
        optimizedFilters.status = 'active';
      }

      // Check if any filters/search/sort are being applied
      const hasFiltersOrSearch = !!(
        optimizedFilters?.searchTerm || 
        optimizedFilters?.city || 
        optimizedFilters?.state || 
        optimizedFilters?.salesRepId ||
        optimizedFilters?.region ||
        optimizedFilters?.tags
      );
      const hasSort = !!sort;
      
      // Always query database when filters/search/sort are applied (skip cache)
      // Cache is only used for default view with no filters/search/sort
      const shouldSkipCache = forceRefresh || hasFiltersOrSearch || hasSort || skipCache;

      // Store current parameters for pagination
      setCurrentFilters(optimizedFilters);
      setCurrentSort(sort);
      setPageSize(pageSize);

      // Generate cache key
      const cacheKey = generateCacheKey(optimizedFilters, sort);
      
      // Try to load from cache only if filters/search/sort haven't changed
      if (!shouldSkipCache) {
        const cachedData = loadFromCache(cacheKey);
        if (cachedData && cachedData.stores.length > 0) {
          console.log('Loading stores from cache:', cachedData.stores.length, 'stores');
          setState(prev => ({
            ...prev,
            stores: cachedData.stores,
            hasMore: cachedData.hasMore,
            lastDoc: undefined, // Can't cache DocumentSnapshot, will need to refetch for pagination
            isLoading: false
          }));
          setSkipCache(false); // Reset skip cache flag
          return;
        }
      }

      // Always fetch from database when filters/search/sort are applied
      console.log('Fetching stores from database with filters:', optimizedFilters, 'sort:', sort);
      const result = await StoreService.getStores(optimizedFilters, sort, pageSize);

      // Save to cache
      saveToCache({
        stores: result.stores,
        filters: optimizedFilters,
        sort,
        hasMore: result.hasMore,
        cacheKey
      });

      setState(prev => ({
        ...prev,
        stores: result.stores,
        hasMore: result.hasMore,
        lastDoc: result.lastDoc,
        isLoading: false
      }));
      
      setSkipCache(false); // Reset skip cache flag
    } catch (error: any) {
      console.error('Error fetching stores:', error);
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: error.message || 'Failed to fetch stores'
      }));
      setSkipCache(false);
    }
  }, [skipCache]);

  /**
   * Load more stores (pagination)
   * Appends to existing stores list
   */
  const loadMore = useCallback(async () => {
    // Get current state from ref to avoid stale closures
    const currentState = stateRef.current;
    
    // Check if we can load more
    if (!currentState.hasMore || currentState.isLoading) {
      console.log('Cannot load more:', { hasMore: currentState.hasMore, isLoading: currentState.isLoading });
      return;
    }

    try {
      // Set loading state
      setState(prev => ({ ...prev, isLoading: true, error: null }));

      // Get current filters, sort, and page size from refs
      const filters = filtersRef.current;
      const sort = sortRef.current;
      const size = pageSizeRef.current;

      let result;
      let updatedStores: Store[];
      
      if (!currentState.lastDoc) {
        // If we don't have a lastDoc (e.g., loaded from cache), we need to refetch
        // with the last document from the current stores list as the cursor
        const currentCount = currentState.stores.length;
        if (currentCount === 0) {
          console.warn('Cannot load more: No stores to use as cursor');
          setState(prev => ({ ...prev, isLoading: false }));
          return;
        }
        
        // Use the last store in the current list as the cursor
        const lastStore = currentState.stores[currentCount - 1];
        console.log('Loading more: No lastDoc, refetching with last store as cursor. Current count:', currentCount);
        
        // Refetch from beginning with larger page size to get cursor
        const refetchResult = await StoreService.getStores(
          filters,
          sort,
          currentCount + size
        );
        
        // If we got more stores than we had, use the new ones
        if (refetchResult.stores.length > currentCount) {
          // Get only the new stores (after the ones we already have)
          const newStores = refetchResult.stores.slice(currentCount);
          updatedStores = [...currentState.stores, ...newStores];
          result = {
            stores: newStores,
            hasMore: refetchResult.hasMore,
            lastDoc: refetchResult.lastDoc
          };
        } else {
          // No new stores found
          updatedStores = currentState.stores;
          result = {
            stores: [],
            hasMore: false,
            lastDoc: refetchResult.lastDoc
          };
        }
      } else {
        // Normal pagination with cursor - fetch next page
        console.log('Loading more: Using cursor, fetching next', size, 'stores');
        result = await StoreService.getStores(
          filters,
          sort,
          size,
          currentState.lastDoc
        );

        // Append new stores to existing list
        updatedStores = [...currentState.stores, ...result.stores];
        console.log('Appended stores. Total now:', updatedStores.length);
      }

      // Update state with new stores using functional update
      setState(prev => ({
        ...prev,
        stores: updatedStores,
        hasMore: result.hasMore,
        lastDoc: result.lastDoc,
        isLoading: false
      }));

      // Update cache with current stores list
      const cacheKey = generateCacheKey(filters, sort);
      saveToCache({
        stores: updatedStores,
        filters: filters,
        sort: sort,
        hasMore: result.hasMore,
        cacheKey
      });
      
      console.log('Load more complete. Total stores:', updatedStores.length, 'Has more:', result.hasMore);
    } catch (error: any) {
      console.error('Error loading more stores:', error);
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: error.message || 'Failed to load more stores'
      }));
    }
  }, []); // Empty deps - using refs for all values

  /**
   * Fetch a single store by ID
   */
  const fetchStoreById = useCallback(async (storeId: string) => {
    try {
      setState(prev => ({ ...prev, isLoading: true, error: null }));

      const store = await StoreService.getStoreById(storeId);

      setState(prev => ({
        ...prev,
        currentStore: store,
        isLoading: false
      }));
    } catch (error: any) {
      console.error('Error fetching store:', error);
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: error.message || 'Failed to fetch store'
      }));
    }
  }, []);

  /**
   * Fetch store statistics
   */
  const fetchStatistics = useCallback(async () => {
    try {
      setState(prev => ({ ...prev, isLoadingStatistics: true, error: null }));

      const stats = await StoreService.getStoreStatistics();

      setState(prev => ({
        ...prev,
        statistics: stats,
        isLoadingStatistics: false
      }));
    } catch (error: any) {
      console.error('Error fetching statistics:', error);
      setState(prev => ({
        ...prev,
        isLoadingStatistics: false,
        error: error.message || 'Failed to fetch statistics'
      }));
    }
  }, []);

  /**
   * Create a new store
   */
  const createStore = useCallback(async (
    storeData: StorePayload,
    userId: string
  ): Promise<StoreActionResult> => {
    try {
      setState(prev => ({ ...prev, isPerformingAction: true, error: null }));

      const result = await StoreService.createStore(storeData, userId);

      if (result.success) {
        // Refresh store list
        await fetchStores(currentFilters, currentSort, pageSize);
      }

      setState(prev => ({ ...prev, isPerformingAction: false }));
      return result;
    } catch (error: any) {
      console.error('Error creating store:', error);
      setState(prev => ({
        ...prev,
        isPerformingAction: false,
        error: error.message || 'Failed to create store'
      }));
      return {
        success: false,
        error: error.message || 'Failed to create store'
      };
    }
  }, [fetchStores, currentFilters, currentSort, pageSize]);

  /**
   * Update an existing store
   */
  const updateStore = useCallback(async (
    storeId: string,
    updates: Partial<StorePayload>,
    userId: string
  ): Promise<StoreActionResult> => {
    try {
      setState(prev => ({ ...prev, isPerformingAction: true, error: null }));

      const result = await StoreService.updateStore(storeId, updates, userId);

      if (result.success) {
        // Update local state
        setState(prev => ({
          ...prev,
          stores: prev.stores.map(store =>
            store.id === storeId ? { ...store, ...updates } : store
          ),
          currentStore: prev.currentStore?.id === storeId
            ? { ...prev.currentStore, ...updates }
            : prev.currentStore
        }));
      }

      setState(prev => ({ ...prev, isPerformingAction: false }));
      return result;
    } catch (error: any) {
      console.error('Error updating store:', error);
      setState(prev => ({
        ...prev,
        isPerformingAction: false,
        error: error.message || 'Failed to update store'
      }));
      return {
        success: false,
        error: error.message || 'Failed to update store'
      };
    }
  }, []);

  /**
   * Delete a store
   */
  const deleteStore = useCallback(async (
    storeId: string,
    userId: string,
    hardDelete: boolean = false
  ): Promise<StoreActionResult> => {
    try {
      setState(prev => ({ ...prev, isPerformingAction: true, error: null }));

      const result = await StoreService.deleteStore(storeId, userId, hardDelete);

      if (result.success) {
        // Remove from local state if hard deleted, otherwise update status
        setState(prev => ({
          ...prev,
          stores: hardDelete
            ? prev.stores.filter(store => store.id !== storeId)
            : prev.stores.map(store =>
                store.id === storeId ? { ...store, status: 'archived' } : store
              ),
          currentStore: prev.currentStore?.id === storeId ? null : prev.currentStore
        }));
      }

      setState(prev => ({ ...prev, isPerformingAction: false }));
      return result;
    } catch (error: any) {
      console.error('Error deleting store:', error);
      setState(prev => ({
        ...prev,
        isPerformingAction: false,
        error: error.message || 'Failed to delete store'
      }));
      return {
        success: false,
        error: error.message || 'Failed to delete store'
      };
    }
  }, []);

  /**
   * Change store status
   */
  const changeStoreStatus = useCallback(async (
    storeId: string,
    newStatus: StoreStatus,
    userId: string
  ): Promise<StoreActionResult> => {
    try {
      setState(prev => ({ ...prev, isPerformingAction: true, error: null }));

      const result = await StoreService.changeStoreStatus(storeId, newStatus, userId);

      if (result.success) {
        setState(prev => ({
          ...prev,
          stores: prev.stores.map(store =>
            store.id === storeId ? { ...store, status: newStatus } : store
          )
        }));
      }

      setState(prev => ({ ...prev, isPerformingAction: false }));
      return result;
    } catch (error: any) {
      console.error('Error changing store status:', error);
      setState(prev => ({
        ...prev,
        isPerformingAction: false,
        error: error.message || 'Failed to change store status'
      }));
      return {
        success: false,
        error: error.message || 'Failed to change store status'
      };
    }
  }, []);

  /**
   * Assign sales representative to a store
   */
  const assignSalesRep = useCallback(async (
    assignment: StoreAssignment,
    userId: string
  ): Promise<StoreActionResult> => {
    try {
      setState(prev => ({ ...prev, isPerformingAction: true, error: null }));

      const result = await StoreService.assignSalesRep(assignment, userId);

      if (result.success) {
        setState(prev => ({
          ...prev,
          stores: prev.stores.map(store =>
            store.id === assignment.storeId
              ? {
                  ...store,
                  salespersonNo: assignment.salesRepId,
                  salesPersonName: assignment.salesRepName
                }
              : store
          )
        }));
      }

      setState(prev => ({ ...prev, isPerformingAction: false }));
      return result;
    } catch (error: any) {
      console.error('Error assigning sales rep:', error);
      setState(prev => ({
        ...prev,
        isPerformingAction: false,
        error: error.message || 'Failed to assign sales representative'
      }));
      return {
        success: false,
        error: error.message || 'Failed to assign sales representative'
      };
    }
  }, []);

  /**
   * Perform bulk action on multiple stores
   */
  const performBulkAction = useCallback(async (
    bulkAction: BulkStoreAction,
    userId: string
  ): Promise<StoreActionResult[]> => {
    try {
      setState(prev => ({ ...prev, isPerformingAction: true, error: null }));

      const results = await StoreService.performBulkAction(bulkAction, userId);

      // Refresh stores after bulk action
      await fetchStores(currentFilters, currentSort, pageSize);

      setState(prev => ({ ...prev, isPerformingAction: false }));
      return results;
    } catch (error: any) {
      console.error('Error performing bulk action:', error);
      setState(prev => ({
        ...prev,
        isPerformingAction: false,
        error: error.message || 'Failed to perform bulk action'
      }));
      return [];
    }
  }, [fetchStores, currentFilters, currentSort, pageSize]);

  /**
   * Refresh stores list
   * Clears cache and fetches fresh data from database
   */
  const refreshStores = useCallback(async () => {
    // Clear cache
    clearCache();
    setSkipCache(true);
    
    // Fetch fresh data from database
    await fetchStores(currentFilters, currentSort, pageSize, true);
  }, [fetchStores, currentFilters, currentSort, pageSize]);

  /**
   * Clear error message
   */
  const clearError = useCallback(() => {
    setState(prev => ({ ...prev, error: null }));
  }, []);

  /**
   * Set current store
   */
  const setCurrentStore = useCallback((store: Store | null) => {
    setState(prev => ({ ...prev, currentStore: store }));
  }, []);

  return {
    // State
    ...state,
    
    // Fetch operations
    fetchStores,
    fetchStoreById,
    fetchStatistics,
    loadMore,
    
    // CRUD operations
    createStore,
    updateStore,
    deleteStore,
    
    // Status operations
    changeStoreStatus,
    
    // Assignment operations
    assignSalesRep,
    
    // Bulk operations
    performBulkAction,
    
    // Utility operations
    refreshStores,
    clearError,
    setCurrentStore
  };
}

